/**
 * LegalFlow — pipelines d'ingestion smartdata (CronJobs GKE) par pays :
 * état, planning, historique, volumes des collections, erreurs, relance.
 * API : /api/legalflow/* (backend/chainlit/api/legalflow.py).
 */
import {
  Activity,
  AlertTriangle,
  ArrowUpDown,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Database,
  Download,
  FileText,
  LayoutGrid,
  List,
  Loader2,
  Pause,
  Play,
  RefreshCw,
  RotateCcw,
  Search
} from 'lucide-react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Page from 'pages/Page';

import { ChainlitContext, useAuth } from '@chainlit/react-client';

import { staffAccess } from '@/components/admin/sections';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';

// ── Types (miroir de /api/legalflow/overview) ────────────────────────────────

type Health =
  | 'healthy'
  | 'failing'
  | 'stuck'
  | 'running'
  | 'suspended'
  | 'never_run';
type RunStatus = 'succeeded' | 'failed' | 'running' | 'pending';

interface Run {
  name: string;
  status: RunStatus;
  start: string | null;
  end: string | null;
  duration: number | null;
  failed: number;
  manual: boolean;
  error: string | null;
}

interface Pipeline {
  name: string;
  title: string;
  classified: boolean;
  country: string | null;
  kind: string | null;
  source: string;
  collections: string[];
  public: boolean;
  description: string;
  schedule: string;
  timezone: string | null;
  suspended: boolean;
  health: Health;
  last_schedule: string | null;
  last_success: string | null;
  next_run: string | null;
  image: string;
  runs: Run[];
  failures_7d: number;
}

interface CollectionStat {
  count: number | null;
  delta_24h: number | null;
  delta_7d: number | null;
}

interface Overview {
  namespace: string;
  generated_at: string;
  countries: Record<string, { label: string; flag: string }>;
  kinds: Record<string, string>;
  pipelines: Pipeline[];
  collections: Record<string, CollectionStat>;
  orphan_jobs: Run[];
  not_deployed: string[];
}

// ── Mise en forme ─────────────────────────────────────────────────────────────

const NF = new Intl.NumberFormat('fr-FR');
const RTF = new Intl.RelativeTimeFormat('fr', { numeric: 'auto' });
const DAYS = [
  'dimanche',
  'lundi',
  'mardi',
  'mercredi',
  'jeudi',
  'vendredi',
  'samedi'
];

function relative(iso: string | null): string {
  if (!iso) return '—';
  const s = (new Date(iso).getTime() - Date.now()) / 1000;
  const a = Math.abs(s);
  if (a < 60) return RTF.format(Math.round(s), 'second');
  if (a < 3600) return RTF.format(Math.round(s / 60), 'minute');
  if (a < 86400) return RTF.format(Math.round(s / 3600), 'hour');
  return RTF.format(Math.round(s / 86400), 'day');
}

function duration(sec: number | null): string {
  if (sec == null) return '';
  if (sec < 60) return `${sec} s`;
  if (sec < 3600) return `${Math.round(sec / 60)} min`;
  return `${Math.floor(sec / 3600)} h ${Math.round((sec % 3600) / 60)} min`;
}

/** Libellé lisible des expressions cron courantes ; sinon l'expression brute. */
function humanSchedule(expr: string): string {
  const f = expr.trim().split(/\s+/);
  if (f.length !== 5) return expr;
  const [mi, h, dom, mo, dow] = f;
  const at = () => `${h.padStart(2, '0')}:${mi.padStart(2, '0')}`;
  if (dom !== '*' || mo !== '*') return expr;
  if (/^\*\/\d+$/.test(mi) && h === '*' && dow === '*')
    return `toutes les ${mi.slice(2)} min`;
  if (mi === '0' && h === '*' && dow === '*') return 'toutes les heures';
  if (/^\d+$/.test(mi) && /^\*\/\d+$/.test(h) && dow === '*')
    return `toutes les ${h.slice(2)} h`;
  if (/^\d+$/.test(mi) && /^\d+$/.test(h)) {
    if (dow === '*') return `chaque jour à ${at()}`;
    if (/^\d$/.test(dow)) return `chaque ${DAYS[Number(dow) % 7]} à ${at()}`;
  }
  return expr;
}

const HEALTH: Record<
  Health,
  { label: string; cls: string; icon: typeof CheckCircle2 }
> = {
  healthy: {
    label: 'OK',
    cls: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
    icon: CheckCircle2
  },
  failing: {
    label: 'En erreur',
    cls: 'bg-red-500/10 text-red-700 dark:text-red-300',
    icon: AlertTriangle
  },
  stuck: {
    label: 'Bloqué',
    cls: 'bg-orange-500/10 text-orange-700 dark:text-orange-300',
    icon: AlertTriangle
  },
  running: {
    label: 'En cours',
    cls: 'bg-blue-500/10 text-blue-700 dark:text-blue-300',
    icon: Loader2
  },
  suspended: {
    label: 'Suspendu',
    cls: 'bg-muted text-muted-foreground',
    icon: Pause
  },
  never_run: {
    label: 'Jamais lancé',
    cls: 'bg-muted text-muted-foreground',
    icon: Clock
  }
};

const RUN_DOT: Record<RunStatus, string> = {
  succeeded: 'bg-emerald-500',
  failed: 'bg-red-500',
  running: 'bg-blue-500 animate-pulse',
  pending: 'bg-muted-foreground/40'
};

const RUN_LABEL: Record<RunStatus, string> = {
  succeeded: 'réussi',
  failed: 'échec',
  running: 'en cours',
  pending: 'en attente'
};

function HealthBadge({ health }: { health: Health }) {
  const h = HEALTH[health];
  const Icon = h.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${h.cls}`}
    >
      <Icon
        className={`h-3.5 w-3.5 ${health === 'running' ? 'animate-spin' : ''}`}
        aria-hidden="true"
      />
      {h.label}
    </span>
  );
}

function Delta({ value }: { value: number | null }) {
  if (value == null) return <span className="text-muted-foreground">—</span>;
  if (value === 0) return <span className="text-muted-foreground">0</span>;
  return (
    <span
      className={
        value > 0
          ? 'text-emerald-700 dark:text-emerald-300'
          : 'text-red-700 dark:text-red-300'
      }
    >
      {value > 0 ? '+' : ''}
      {NF.format(value)}
    </span>
  );
}

// ── Agrégats ──────────────────────────────────────────────────────────────────

const HEALTH_ORDER: Health[] = [
  'failing',
  'stuck',
  'running',
  'healthy',
  'never_run',
  'suspended'
];

const UNCLASSIFIED = 'unclassified';

type View = 'list' | 'cards';
type SortKey =
  | 'health'
  | 'title'
  | 'kind'
  | 'last'
  | 'next'
  | 'failures'
  | 'docs'
  | 'd24'
  | 'd7';

const VIEW_KEY = 'legalflow_view';

function readView(): View {
  try {
    return localStorage.getItem(VIEW_KEY) === 'cards' ? 'cards' : 'list';
  } catch {
    return 'list';
  }
}

/** Somme d'un champ sur des collections ; null si aucune valeur connue. */
function sumCollections(
  names: string[],
  stats: Record<string, CollectionStat>,
  field: keyof CollectionStat
): number | null {
  let total: number | null = null;
  for (const n of names) {
    const v = stats[n]?.[field];
    if (v != null) total = (total ?? 0) + v;
  }
  return total;
}

function countryKey(p: Pipeline): string {
  return p.classified && p.country ? p.country : UNCLASSIFIED;
}

function sortValue(
  p: Pipeline,
  key: SortKey,
  stats: Record<string, CollectionStat>
): number | string {
  switch (key) {
    case 'health':
      return HEALTH_ORDER.indexOf(p.health);
    case 'title':
      return p.title.toLowerCase();
    case 'kind':
      return p.kind ?? '~';
    case 'last':
      return p.runs[0]?.start ? -new Date(p.runs[0].start).getTime() : 0;
    case 'next':
      return p.next_run ? new Date(p.next_run).getTime() : Infinity;
    case 'failures':
      return -p.failures_7d;
    case 'docs':
      return -(sumCollections(p.collections, stats, 'count') ?? -1);
    case 'd24':
      return -(sumCollections(p.collections, stats, 'delta_24h') ?? -Infinity);
    case 'd7':
      return -(sumCollections(p.collections, stats, 'delta_7d') ?? -Infinity);
  }
}

function csvCell(v: unknown): string {
  const s = v == null ? '' : String(v);
  return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function exportCsv(data: Overview, rows: Pipeline[]) {
  const head = [
    'pays',
    'type',
    'cronjob',
    'titre',
    'statut',
    'planning',
    'fuseau',
    'derniere_execution',
    'dernier_statut',
    'duree_s',
    'prochaine_execution',
    'echecs_7j',
    'documents',
    'delta_24h',
    'delta_7j',
    'collections',
    'source',
    'image',
    'derniere_erreur'
  ];
  const lines = rows.map((p) => {
    const last = p.runs[0];
    const c = data.collections;
    return [
      p.country ?? '',
      p.kind ?? '',
      p.name,
      p.title,
      p.health,
      p.schedule,
      p.timezone ?? '',
      last?.start ?? '',
      last?.status ?? '',
      last?.duration ?? '',
      p.next_run ?? '',
      p.failures_7d,
      sumCollections(p.collections, c, 'count') ?? '',
      sumCollections(p.collections, c, 'delta_24h') ?? '',
      sumCollections(p.collections, c, 'delta_7d') ?? '',
      p.collections.join(' '),
      p.source,
      p.image,
      last?.error ?? ''
    ]
      .map(csvCell)
      .join(',');
  });
  const blob = new Blob([[head.join(','), ...lines].join('\n')], {
    type: 'text/csv;charset=utf-8'
  });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `legalflow-${new Date().toISOString().slice(0, 16)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ── Composants ────────────────────────────────────────────────────────────────

function RunBars({
  runs,
  onOpen,
  size = 'md'
}: {
  runs: Run[];
  /** Absent : lecture seule, pas d'accès aux logs */
  onOpen?: (job: string) => void;
  size?: 'sm' | 'md';
}) {
  if (runs.length === 0)
    return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <div className="flex items-center gap-1">
      {[...runs].reverse().map((r) => (
        <button
          key={r.name}
          onClick={onOpen ? () => onOpen(r.name) : undefined}
          disabled={!onOpen}
          title={`${RUN_LABEL[r.status]}${r.manual ? ' (manuel)' : ''} — ${
            r.start ? new Date(r.start).toLocaleString('fr-FR') : ''
          }${r.duration != null ? ` · ${duration(r.duration)}` : ''}${
            r.error ? ` — ${r.error}` : ''
          }`}
          aria-label={`Logs de l'exécution ${r.name} (${RUN_LABEL[r.status]})`}
          className={`${size === 'sm' ? 'h-3 w-3' : 'h-3 w-6'} rounded-sm ${
            RUN_DOT[r.status]
          } ${
            r.manual
              ? 'ring-2 ring-violet-500/60 ring-offset-1 ring-offset-card'
              : ''
          }`}
        />
      ))}
    </div>
  );
}

function Actions({
  p,
  busy,
  act
}: {
  p: Pipeline;
  busy: boolean;
  act: (p: Pipeline, action: 'run' | 'suspend' | 'resume') => void;
}) {
  const btn =
    'rounded-lg border p-1.5 hover:bg-accent disabled:opacity-50 bg-card';
  return (
    <div className="flex shrink-0 justify-end gap-1">
      <button
        onClick={() => act(p, 'run')}
        disabled={busy}
        title="Relancer maintenant"
        aria-label={`Relancer ${p.title}`}
        className={btn}
      >
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        ) : (
          <Play className="h-4 w-4" aria-hidden="true" />
        )}
      </button>
      <button
        onClick={() => act(p, p.suspended ? 'resume' : 'suspend')}
        disabled={busy}
        title={p.suspended ? 'Reprendre le planning' : 'Suspendre le planning'}
        aria-label={`${p.suspended ? 'Reprendre' : 'Suspendre'} ${p.title}`}
        className={btn}
      >
        {p.suspended ? (
          <RotateCcw className="h-4 w-4" aria-hidden="true" />
        ) : (
          <Pause className="h-4 w-4" aria-hidden="true" />
        )}
      </button>
    </div>
  );
}

function CollectionsTable({
  names,
  stats
}: {
  names: string[];
  stats: Record<string, CollectionStat>;
}) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs text-muted-foreground">
          <th className="font-normal">Collection</th>
          <th className="text-right font-normal">Documents</th>
          <th className="text-right font-normal">24 h</th>
          <th className="text-right font-normal">7 j</th>
        </tr>
      </thead>
      <tbody>
        {names.map((name) => {
          const s = stats[name];
          return (
            <tr key={name}>
              <td className="py-0.5 font-mono text-xs">{name}</td>
              <td className="text-right tabular-nums">
                {s?.count != null ? NF.format(s.count) : '—'}
              </td>
              <td className="text-right tabular-nums">
                <Delta value={s?.delta_24h ?? null} />
              </td>
              <td className="text-right tabular-nums">
                <Delta value={s?.delta_7d ?? null} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** Matrice pays × statut : chaque cellule filtre la liste. */
function CountryMatrix({
  data,
  country,
  health,
  onPick
}: {
  data: Overview;
  country: string;
  health: Health | 'all';
  onPick: (country: string, health: Health | 'all') => void;
}) {
  const rows = useMemo(() => {
    const keys = [
      ...Object.keys(data.countries),
      ...(data.pipelines.some((p) => !p.classified) ? [UNCLASSIFIED] : [])
    ];
    const build = (
      label: string,
      flag: string,
      key: string,
      ps: Pipeline[]
    ) => {
      const cols = [...new Set(ps.flatMap((p) => p.collections))];
      const by = Object.fromEntries(
        HEALTH_ORDER.map((h) => [h, ps.filter((p) => p.health === h).length])
      ) as Record<Health, number>;
      return {
        key,
        label,
        flag,
        total: ps.length,
        by,
        failures: ps.reduce((a, p) => a + p.failures_7d, 0),
        docs: sumCollections(cols, data.collections, 'count'),
        d24: sumCollections(cols, data.collections, 'delta_24h'),
        d7: sumCollections(cols, data.collections, 'delta_7d')
      };
    };
    const list = keys
      .map((k) => {
        const c = data.countries[k];
        return build(
          c?.label ?? 'Non classés',
          c?.flag ?? '❔',
          k,
          data.pipelines.filter((p) => countryKey(p) === k)
        );
      })
      .filter((r) => r.total > 0);
    return { list, total: build('Total', '', 'all', data.pipelines) };
  }, [data]);

  const cell = (
    rowKey: string,
    h: Health,
    n: number,
    active: boolean
  ): React.ReactNode => {
    if (n === 0) return <span className="text-muted-foreground/50">·</span>;
    return (
      <button
        onClick={() => onPick(rowKey, active ? 'all' : h)}
        aria-pressed={active}
        title={`${HEALTH[h].label} — filtrer`}
        className={`min-w-[2rem] rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums transition-shadow hover:ring-2 hover:ring-ring/40 ${
          HEALTH[h].cls
        } ${active ? 'ring-2 ring-ring' : ''}`}
      >
        {n}
      </button>
    );
  };

  const line = (r: (typeof rows)['total'], isTotal = false) => {
    const rowActive = country === r.key;
    return (
      <tr
        key={r.key}
        className={`${isTotal ? 'border-t-2 font-semibold' : 'border-b'} ${
          rowActive ? 'bg-accent/60' : ''
        }`}
      >
        <th scope="row" className="py-2 pr-4 text-left font-medium">
          <button
            onClick={() => onPick(r.key, 'all')}
            className="inline-flex items-center gap-2 hover:underline"
            aria-pressed={rowActive}
          >
            {r.flag && <span aria-hidden="true">{r.flag}</span>}
            {r.label}
          </button>
        </th>
        <td className="px-2 py-2 text-right tabular-nums">{r.total}</td>
        {HEALTH_ORDER.map((h) => (
          <td key={h} className="px-2 py-2 text-center">
            {cell(
              r.key,
              h,
              r.by[h],
              (country === r.key || (r.key === 'all' && country === 'all')) &&
                health === h
            )}
          </td>
        ))}
        <td
          className={`px-2 py-2 text-right tabular-nums ${
            r.failures ? 'text-red-700 dark:text-red-300' : ''
          }`}
        >
          {r.failures || <span className="text-muted-foreground/50">·</span>}
        </td>
        <td className="px-2 py-2 text-right tabular-nums">
          {r.docs != null ? NF.format(r.docs) : '—'}
        </td>
        <td className="px-2 py-2 text-right tabular-nums">
          <Delta value={r.d24} />
        </td>
        <td className="px-2 py-2 text-right tabular-nums">
          <Delta value={r.d7} />
        </td>
      </tr>
    );
  };

  return (
    <section
      aria-labelledby="lf-matrix"
      className="rounded-2xl border bg-card p-5 shadow-sm"
    >
      <h2 id="lf-matrix" className="font-semibold">
        Par pays
      </h2>
      <p className="text-sm text-muted-foreground">
        Statut de chaque pipeline, échecs sur 7 jours et volumes collectés.
        Cliquez un chiffre pour filtrer la liste.
      </p>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm">
          <thead>
            <tr className="border-b text-xs text-muted-foreground">
              <th className="py-2 pr-4 text-left font-normal">Pays</th>
              <th className="px-2 py-2 text-right font-normal">Pipelines</th>
              {HEALTH_ORDER.map((h) => (
                <th key={h} className="px-2 py-2 text-center font-normal">
                  {HEALTH[h].label}
                </th>
              ))}
              <th className="px-2 py-2 text-right font-normal">Échecs 7 j</th>
              <th className="px-2 py-2 text-right font-normal">Documents</th>
              <th className="px-2 py-2 text-right font-normal">24 h</th>
              <th className="px-2 py-2 text-right font-normal">7 j</th>
            </tr>
          </thead>
          <tbody>
            {rows.list.map((r) => line(r))}
            {line(rows.total, true)}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SortHeader({
  label,
  k,
  sort,
  setSort,
  align = 'left'
}: {
  label: string;
  k: SortKey;
  sort: SortKey;
  setSort: (k: SortKey) => void;
  align?: 'left' | 'right';
}) {
  const active = sort === k;
  return (
    <th
      scope="col"
      aria-sort={active ? 'ascending' : 'none'}
      className={`whitespace-nowrap px-2 py-2 font-normal ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      <button
        onClick={() => setSort(k)}
        className={`inline-flex items-center gap-1 hover:text-foreground ${
          active ? 'font-semibold text-foreground' : ''
        }`}
      >
        {label}
        <ArrowUpDown
          className={`h-3 w-3 ${active ? 'opacity-100' : 'opacity-40'}`}
          aria-hidden="true"
        />
      </button>
    </th>
  );
}

function PipelineRow({
  p,
  data,
  expanded,
  toggle,
  busy,
  act,
  openLogs
}: {
  p: Pipeline;
  data: Overview;
  expanded: boolean;
  toggle: () => void;
  busy: boolean;
  act?: (p: Pipeline, action: 'run' | 'suspend' | 'resume') => void;
  openLogs?: (job: string) => void;
}) {
  const last = p.runs[0];
  const stats = data.collections;
  const docs = sumCollections(p.collections, stats, 'count');
  const alert = p.health === 'failing' || p.health === 'stuck';
  return (
    <>
      <tr
        className={`border-b align-middle hover:bg-accent/40 ${
          alert ? 'bg-red-500/[0.04]' : ''
        }`}
      >
        <td className="px-2 py-2">
          <button
            onClick={toggle}
            aria-expanded={expanded}
            aria-label={`${expanded ? 'Replier' : 'Déplier'} ${p.title}`}
            className="rounded p-0.5 text-muted-foreground hover:bg-accent"
          >
            {expanded ? (
              <ChevronDown className="h-4 w-4" aria-hidden="true" />
            ) : (
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
        </td>
        <td className="whitespace-nowrap px-2 py-2">
          <HealthBadge health={p.health} />
        </td>
        <td className="max-w-[22rem] px-2 py-2">
          <button onClick={toggle} className="block w-full text-left">
            <span className="flex items-center gap-1.5">
              <span className="truncate font-medium text-foreground">
                {p.title}
              </span>
              {p.public && (
                <span className="shrink-0 rounded-full bg-violet-600/10 px-1.5 py-0.5 text-[10px] text-violet-700 dark:text-violet-300">
                  Public
                </span>
              )}
            </span>
            <span className="block truncate font-mono text-[11px] text-muted-foreground">
              {p.name}
            </span>
          </button>
        </td>
        <td className="whitespace-nowrap px-2 py-2 text-xs text-muted-foreground">
          {p.kind ? data.kinds[p.kind] ?? p.kind : '—'}
        </td>
        <td
          className="whitespace-nowrap px-2 py-2 text-xs"
          title={`${p.schedule}${p.timezone ? ` (${p.timezone})` : ''}`}
        >
          {humanSchedule(p.schedule)}
        </td>
        <td className="whitespace-nowrap px-2 py-2 text-xs">
          {last ? (
            <span
              title={
                last.start ? new Date(last.start).toLocaleString('fr-FR') : ''
              }
            >
              <span
                className={`mr-1.5 inline-block h-2 w-2 rounded-full ${
                  RUN_DOT[last.status]
                }`}
                aria-hidden="true"
              />
              {relative(last.start)}
              {last.duration != null && (
                <span className="text-muted-foreground">
                  {' '}
                  · {duration(last.duration)}
                </span>
              )}
            </span>
          ) : (
            '—'
          )}
        </td>
        <td className="whitespace-nowrap px-2 py-2 text-xs">
          {p.suspended ? (
            <span className="text-muted-foreground">suspendu</span>
          ) : (
            relative(p.next_run)
          )}
        </td>
        <td className="px-2 py-2">
          <RunBars runs={p.runs} onOpen={openLogs} size="sm" />
        </td>
        <td
          className={`px-2 py-2 text-right tabular-nums ${
            p.failures_7d ? 'font-semibold text-red-700 dark:text-red-300' : ''
          }`}
        >
          {p.failures_7d || <span className="text-muted-foreground/50">·</span>}
        </td>
        <td className="px-2 py-2 text-right tabular-nums">
          {docs != null ? NF.format(docs) : '—'}
        </td>
        <td className="px-2 py-2 text-right tabular-nums">
          <Delta value={sumCollections(p.collections, stats, 'delta_24h')} />
        </td>
        <td className="px-2 py-2 text-right tabular-nums">
          <Delta value={sumCollections(p.collections, stats, 'delta_7d')} />
        </td>
        <td className="px-2 py-2">
          {act && <Actions p={p} busy={busy} act={act} />}
        </td>
      </tr>
      {expanded && (
        <tr className="border-b bg-muted/30">
          <td />
          <td colSpan={12} className="px-2 py-4">
            <div className="grid gap-6 lg:grid-cols-2">
              <div className="space-y-3 text-sm">
                {(p.source || p.description) && (
                  <p className="text-muted-foreground">
                    {p.source && (
                      <span className="font-medium text-foreground">
                        {p.source}.{' '}
                      </span>
                    )}
                    {p.description}
                  </p>
                )}
                <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-xs">
                  <dt className="text-muted-foreground">Cron</dt>
                  <dd className="font-mono">
                    {p.schedule}
                    {p.timezone ? ` (${p.timezone})` : ' (UTC)'}
                  </dd>
                  <dt className="text-muted-foreground">Image</dt>
                  <dd className="font-mono">{p.image || '—'}</dd>
                  <dt className="text-muted-foreground">Dernier succès</dt>
                  <dd>
                    {p.last_success
                      ? `${new Date(p.last_success).toLocaleString(
                          'fr-FR'
                        )} (${relative(p.last_success)})`
                      : '—'}
                  </dd>
                  <dt className="text-muted-foreground">Prochaine</dt>
                  <dd>
                    {p.next_run
                      ? new Date(p.next_run).toLocaleString('fr-FR')
                      : '—'}
                  </dd>
                  {last?.error && (
                    <>
                      <dt className="text-muted-foreground">Dernière erreur</dt>
                      <dd className="text-red-700 dark:text-red-300">
                        {last.error}
                      </dd>
                    </>
                  )}
                </dl>
                {p.collections.length > 0 && (
                  <CollectionsTable names={p.collections} stats={stats} />
                )}
              </div>
              <div>
                <p className="mb-2 text-xs text-muted-foreground">
                  Exécutions récentes
                </p>
                {p.runs.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Aucune.</p>
                ) : (
                  <table className="w-full text-xs">
                    <tbody>
                      {p.runs.map((r) => (
                        <tr key={r.name} className="border-b last:border-0">
                          <td className="py-1 pr-2">
                            <span
                              className={`inline-block h-2 w-2 rounded-full ${
                                RUN_DOT[r.status]
                              }`}
                              aria-hidden="true"
                            />
                          </td>
                          <td className="py-1 pr-2">
                            {RUN_LABEL[r.status]}
                            {r.manual ? ' (manuel)' : ''}
                          </td>
                          <td className="py-1 pr-2 tabular-nums">
                            {r.start
                              ? new Date(r.start).toLocaleString('fr-FR')
                              : '—'}
                          </td>
                          <td className="py-1 pr-2 tabular-nums text-muted-foreground">
                            {duration(r.duration)}
                          </td>
                          <td className="max-w-[14rem] truncate py-1 pr-2 text-red-700 dark:text-red-300">
                            {r.error}
                          </td>
                          <td className="py-1 text-right">
                            {openLogs && (
                              <button
                                onClick={() => openLogs(r.name)}
                                className="text-primary underline-offset-2 hover:underline"
                              >
                                Logs
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function PipelineCard({
  p,
  data,
  busy,
  act,
  openLogs
}: {
  p: Pipeline;
  data: Overview;
  busy: boolean;
  act?: (p: Pipeline, action: 'run' | 'suspend' | 'resume') => void;
  openLogs?: (job: string) => void;
}) {
  const c = p.country ? data.countries[p.country] : null;
  const last = p.runs[0];
  return (
    <article className="flex flex-col rounded-2xl border bg-card p-5 shadow-sm">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <HealthBadge health={p.health} />
            {c && (
              <span className="text-xs text-muted-foreground">
                <span aria-hidden="true">{c.flag}</span> {c.label}
              </span>
            )}
            {p.kind && (
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
                {data.kinds[p.kind] ?? p.kind}
              </span>
            )}
            {p.public && (
              <span className="rounded-full bg-violet-600/10 px-2 py-0.5 text-xs text-violet-700 dark:text-violet-300">
                Public
              </span>
            )}
            {!p.classified && (
              <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-xs text-amber-700 dark:text-amber-300">
                Non classé
              </span>
            )}
          </div>
          <h3 className="mt-2 truncate text-base font-semibold text-foreground">
            {p.title}
          </h3>
          <p className="truncate font-mono text-xs text-muted-foreground">
            {p.name}
            {p.image ? ` · ${p.image}` : ''}
          </p>
        </div>
        {act && <Actions p={p} busy={busy} act={act} />}
      </header>

      {(p.source || p.description) && (
        <p className="mt-2 text-sm text-muted-foreground">
          {p.source && (
            <span className="font-medium text-foreground">{p.source}. </span>
          )}
          {p.description}
        </p>
      )}

      <dl className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
        <div>
          <dt className="text-xs text-muted-foreground">Planning</dt>
          <dd
            className="font-medium"
            title={`${p.schedule}${p.timezone ? ` (${p.timezone})` : ''}`}
          >
            {humanSchedule(p.schedule)}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Dernière exécution</dt>
          <dd className="font-medium">
            {last
              ? `${relative(last.start)}${
                  last.duration != null ? ` · ${duration(last.duration)}` : ''
                }`
              : '—'}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Prochaine</dt>
          <dd className="font-medium">
            {p.suspended ? 'suspendu' : relative(p.next_run)}
          </dd>
        </div>
      </dl>

      {p.runs.length > 0 && (
        <div className="mt-4">
          <p className="text-xs text-muted-foreground">
            Exécutions récentes
            {p.failures_7d ? ` — ${p.failures_7d} échec(s) sur 7 jours` : ''}
          </p>
          <div className="mt-1.5">
            <RunBars runs={p.runs} onOpen={openLogs} />
          </div>
        </div>
      )}

      {p.collections.length > 0 && (
        <div className="mt-4 border-t pt-3">
          <CollectionsTable names={p.collections} stats={data.collections} />
        </div>
      )}
    </article>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function LegalFlow() {
  const apiClient = useContext(ChainlitContext);
  const { isAuthenticated, isReady, user } = useAuth();
  const navigate = useNavigate();
  // Viewer : lecture seule (ni relance, ni suspension, ni logs des pods)
  const canOperate = staffAccess(user?.metadata).role === 'admin';

  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [country, setCountry] = useState<string>('all');
  const [health, setHealth] = useState<Health | 'all'>('all');
  const [kind, setKind] = useState<string>('all');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortKey>('health');
  const [view, setViewState] = useState<View>(readView);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(
    null
  );
  const [logs, setLogs] = useState<{
    job: string;
    text: string | null;
    error?: string;
  } | null>(null);

  const setView = (v: View) => {
    setViewState(v);
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      /* stockage indisponible : la vue n'est simplement pas mémorisée */
    }
  };

  useEffect(() => {
    if (isReady && !isAuthenticated) navigate('/login');
  }, [isReady, isAuthenticated, navigate]);

  const api = useCallback(
    (path: string, init?: RequestInit) =>
      fetch(apiClient.buildEndpoint(`/api/legalflow${path}`), {
        credentials: 'include',
        ...init,
        headers: {
          'Content-Type': 'application/json',
          ...(init?.headers || {})
        }
      }).then(async (r) => {
        const body = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(body.detail || `HTTP ${r.status}`);
        return body;
      }),
    [apiClient]
  );

  const load = useCallback(() => {
    setLoading(true);
    api('/overview')
      .then((d: Overview) => {
        setData(d);
        setError(null);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [api]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  const act = async (p: Pipeline, action: 'run' | 'suspend' | 'resume') => {
    const verb =
      action === 'run'
        ? 'Relancer maintenant'
        : action === 'suspend'
        ? 'Suspendre'
        : 'Reprendre';
    if (!window.confirm(`${verb} « ${p.title} » ?`)) return;
    setBusy(p.name);
    setNotice(null);
    try {
      if (action === 'run') {
        const r = await api(`/pipelines/${p.name}/run`, { method: 'POST' });
        setNotice({ ok: true, text: `Exécution lancée : ${r.job}` });
      } else {
        await api(`/pipelines/${p.name}/suspend`, {
          method: 'POST',
          body: JSON.stringify({ suspend: action === 'suspend' })
        });
        setNotice({
          ok: true,
          text: `${p.title} ${action === 'suspend' ? 'suspendu' : 'repris'}`
        });
      }
      load();
    } catch (e) {
      setNotice({
        ok: false,
        text: `${verb} « ${p.title} » : ${(e as Error).message}`
      });
    } finally {
      setBusy(null);
    }
  };

  const openLogs = (job: string) => {
    setLogs({ job, text: null });
    api(`/runs/${job}/logs?tail=400`)
      .then((r) => setLogs({ job, text: r.logs || '(vide)' }))
      .catch((e: Error) => setLogs({ job, text: null, error: e.message }));
  };

  const toggle = (name: string) =>
    setExpanded((s) => {
      const n = new Set(s);
      if (n.has(name)) n.delete(name);
      else n.add(name);
      return n;
    });

  const pipelines = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    const rows = data.pipelines.filter(
      (p) =>
        (country === 'all' || countryKey(p) === country) &&
        (health === 'all' || p.health === health) &&
        (kind === 'all' || p.kind === kind) &&
        (!q ||
          `${p.title} ${p.name} ${p.source} ${p.collections.join(' ')}`
            .toLowerCase()
            .includes(q))
    );
    const val = (p: Pipeline) => sortValue(p, sort, data.collections);
    // tri stable : à égalité, l'ordre d'urgence renvoyé par l'API est conservé
    return [...rows].sort((a, b) => {
      const va = val(a);
      const vb = val(b);
      return va < vb ? -1 : va > vb ? 1 : 0;
    });
  }, [data, country, health, kind, query, sort]);

  /** Lignes groupées par pays, dans l'ordre du registre. */
  const groups = useMemo(() => {
    if (!data) return [];
    const keys = [...Object.keys(data.countries), UNCLASSIFIED];
    return keys
      .map((k) => ({
        key: k,
        label: data.countries[k]?.label ?? 'Non classés',
        flag: data.countries[k]?.flag ?? '❔',
        rows: pipelines.filter((p) => countryKey(p) === k)
      }))
      .filter((g) => g.rows.length > 0);
  }, [data, pipelines]);

  const healthCounts = useMemo(() => {
    const scoped = (data?.pipelines || []).filter(
      (p) => country === 'all' || countryKey(p) === country
    );
    return Object.fromEntries(
      HEALTH_ORDER.map((h) => [h, scoped.filter((p) => p.health === h).length])
    ) as Record<Health, number>;
  }, [data, country]);

  const alerts = (data?.pipelines || []).filter(
    (p) => p.health === 'failing' || p.health === 'stuck'
  );
  const failedOrphans = (data?.orphan_jobs || []).filter(
    (j) => j.status === 'failed' || j.status === 'running'
  );

  const filtered =
    country !== 'all' || health !== 'all' || kind !== 'all' || query.trim();

  const chip = (active: boolean) =>
    `inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
      active
        ? 'border-primary bg-primary text-primary-foreground'
        : 'bg-card hover:bg-accent'
    }`;

  const allExpanded =
    pipelines.length > 0 && pipelines.every((p) => expanded.has(p.name));

  return (
    <Page>
      <>
        <div className="w-full overflow-y-auto">
          {/* En-tête */}
          <section className="border-b bg-gradient-to-br from-violet-600/10 via-background to-sky-500/10">
            <div className="mx-auto flex max-w-[96rem] flex-wrap items-end justify-between gap-4 px-6 py-6">
              <div>
                <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-violet-600 dark:text-violet-300">
                  <Activity className="h-4 w-4" aria-hidden="true" /> LegalFlow
                </p>
                <h1 className="mt-1 text-2xl font-bold tracking-tight text-foreground">
                  Pipelines d'ingestion
                </h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Ce que SmartLawyer collecte, par pays : état des crons,
                  planning, volumes et erreurs.
                </p>
              </div>
              <button
                onClick={load}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg border bg-card px-3 py-2 text-sm font-medium hover:bg-accent disabled:opacity-60"
              >
                <RefreshCw
                  className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`}
                  aria-hidden="true"
                />
                {data
                  ? `Mis à jour ${relative(data.generated_at)}`
                  : 'Actualiser'}
              </button>
            </div>
          </section>

          <div className="mx-auto max-w-[96rem] space-y-6 px-6 py-6">
            {error && (
              <div
                role="alert"
                className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200"
              >
                Impossible de charger LegalFlow : {error}
              </div>
            )}
            {notice && (
              <div
                role="status"
                className={`rounded-xl border px-4 py-3 text-sm ${
                  notice.ok
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-200'
                    : 'border-red-200 bg-red-50 text-red-800 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200'
                }`}
              >
                {notice.text}
              </div>
            )}

            {!data && !error && (
              <div className="space-y-3">
                <div className="h-40 animate-pulse rounded-2xl bg-muted" />
                <div className="h-96 animate-pulse rounded-2xl bg-muted" />
              </div>
            )}

            {data && (
              <>
                <CountryMatrix
                  data={data}
                  country={country}
                  health={health}
                  onPick={(c, h) => {
                    setCountry(c);
                    setHealth(h);
                  }}
                />

                {/* Alertes */}
                {(alerts.length > 0 ||
                  failedOrphans.length > 0 ||
                  data.not_deployed.length > 0) && (
                  <section
                    aria-labelledby="lf-alerts"
                    className="rounded-2xl border border-red-200 bg-red-50/60 p-5 dark:border-red-900/60 dark:bg-red-950/20"
                  >
                    <h2
                      id="lf-alerts"
                      className="flex items-center gap-2 font-semibold text-red-800 dark:text-red-200"
                    >
                      <AlertTriangle className="h-4 w-4" aria-hidden="true" /> À
                      traiter
                    </h2>
                    <ul className="mt-3 space-y-2 text-sm">
                      {alerts.map((p) => {
                        const last = p.runs[0];
                        const c = p.country ? data.countries[p.country] : null;
                        return (
                          <li
                            key={p.name}
                            className="flex flex-wrap items-center gap-x-3 gap-y-1"
                          >
                            <HealthBadge health={p.health} />
                            {c && <span aria-hidden="true">{c.flag}</span>}
                            <span className="font-medium text-foreground">
                              {p.title}
                            </span>
                            <span className="text-muted-foreground">
                              {p.health === 'stuck'
                                ? `job actif depuis ${relative(
                                    last?.start ?? null
                                  ).replace('il y a ', '')}`
                                : `dernier échec ${relative(
                                    last?.start ?? null
                                  )}`}
                              {last?.error ? ` — ${last.error}` : ''}
                            </span>
                            {canOperate && last && (
                              <button
                                onClick={() => openLogs(last.name)}
                                className="text-primary underline-offset-2 hover:underline"
                              >
                                Voir les logs
                              </button>
                            )}
                          </li>
                        );
                      })}
                      {failedOrphans.map((j) => (
                        <li
                          key={j.name}
                          className="flex flex-wrap items-center gap-x-3 gap-y-1"
                        >
                          <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium">
                            Job manuel
                          </span>
                          <span className="font-mono text-xs text-foreground">
                            {j.name}
                          </span>
                          <span className="text-muted-foreground">
                            {RUN_LABEL[j.status]} {relative(j.start)}
                          </span>
                          {canOperate && (
                            <button
                              onClick={() => openLogs(j.name)}
                              className="text-primary underline-offset-2 hover:underline"
                            >
                              Voir les logs
                            </button>
                          )}
                        </li>
                      ))}
                      {data.not_deployed.length > 0 && (
                        <li className="text-muted-foreground">
                          Classés mais absents du cluster :{' '}
                          <span className="font-mono text-xs">
                            {data.not_deployed.join(', ')}
                          </span>
                        </li>
                      )}
                    </ul>
                  </section>
                )}

                {/* Barre d'outils */}
                <div className="space-y-3">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div
                      className="flex flex-wrap items-center gap-1.5"
                      role="group"
                      aria-label="Statut"
                    >
                      <button
                        className={chip(health === 'all')}
                        onClick={() => setHealth('all')}
                        aria-pressed={health === 'all'}
                      >
                        Tous les statuts
                      </button>
                      {HEALTH_ORDER.filter((h) => healthCounts[h] > 0).map(
                        (h) => (
                          <button
                            key={h}
                            className={chip(health === h)}
                            onClick={() => setHealth(health === h ? 'all' : h)}
                            aria-pressed={health === h}
                          >
                            {HEALTH[h].label}
                            <span className="tabular-nums opacity-70">
                              {healthCounts[h]}
                            </span>
                          </button>
                        )
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <select
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        aria-label="Pays"
                        className="rounded-lg border bg-card px-3 py-2 text-sm"
                      >
                        <option value="all">Tous les pays</option>
                        {Object.entries(data.countries).map(([k, c]) => (
                          <option key={k} value={k}>
                            {c.flag} {c.label}
                          </option>
                        ))}
                        {data.pipelines.some((p) => !p.classified) && (
                          <option value={UNCLASSIFIED}>Non classés</option>
                        )}
                      </select>
                      <select
                        value={kind}
                        onChange={(e) => setKind(e.target.value)}
                        aria-label="Type de pipeline"
                        className="rounded-lg border bg-card px-3 py-2 text-sm"
                      >
                        <option value="all">Tous les types</option>
                        {Object.entries(data.kinds).map(([k, v]) => (
                          <option key={k} value={k}>
                            {v}
                          </option>
                        ))}
                      </select>
                      <label className="relative">
                        <span className="sr-only">Rechercher</span>
                        <Search
                          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                          aria-hidden="true"
                        />
                        <input
                          value={query}
                          onChange={(e) => setQuery(e.target.value)}
                          placeholder="Pipeline, source, collection…"
                          className="w-60 rounded-lg border bg-card py-2 pl-9 pr-3 text-sm"
                        />
                      </label>
                      <div
                        className="inline-flex rounded-lg border bg-card p-0.5"
                        role="group"
                        aria-label="Affichage"
                      >
                        {(
                          [
                            ['list', List, 'Liste'],
                            ['cards', LayoutGrid, 'Cartes']
                          ] as const
                        ).map(([v, Icon, label]) => (
                          <button
                            key={v}
                            onClick={() => setView(v)}
                            aria-pressed={view === v}
                            title={label}
                            className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm ${
                              view === v
                                ? 'bg-primary text-primary-foreground'
                                : 'hover:bg-accent'
                            }`}
                          >
                            <Icon className="h-4 w-4" aria-hidden="true" />
                            <span className="hidden sm:inline">{label}</span>
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={() => exportCsv(data, pipelines)}
                        className="inline-flex items-center gap-1.5 rounded-lg border bg-card px-3 py-2 text-sm hover:bg-accent"
                        title="Exporter les lignes affichées en CSV"
                      >
                        <Download className="h-4 w-4" aria-hidden="true" /> CSV
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {pipelines.length} / {data.pipelines.length} pipelines
                    {filtered && (
                      <>
                        {' · '}
                        <button
                          onClick={() => {
                            setCountry('all');
                            setHealth('all');
                            setKind('all');
                            setQuery('');
                          }}
                          className="text-primary underline-offset-2 hover:underline"
                        >
                          Réinitialiser les filtres
                        </button>
                      </>
                    )}
                    {view === 'list' && pipelines.length > 0 && (
                      <>
                        {' · '}
                        <button
                          onClick={() =>
                            setExpanded(
                              allExpanded
                                ? new Set()
                                : new Set(pipelines.map((p) => p.name))
                            )
                          }
                          className="text-primary underline-offset-2 hover:underline"
                        >
                          {allExpanded ? 'Tout replier' : 'Tout déplier'}
                        </button>
                      </>
                    )}
                  </p>
                </div>

                {pipelines.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    Aucun pipeline ne correspond aux filtres.
                  </p>
                )}

                {/* Liste */}
                {view === 'list' && pipelines.length > 0 && (
                  <section
                    aria-label="Pipelines"
                    className="overflow-x-auto rounded-2xl border bg-card shadow-sm"
                  >
                    <table className="w-full min-w-[1100px] text-sm">
                      <thead className="sticky top-0 z-10 bg-card">
                        <tr className="border-b text-xs text-muted-foreground">
                          <th className="w-8 px-2 py-2">
                            <span className="sr-only">Détails</span>
                          </th>
                          <SortHeader
                            label="Statut"
                            k="health"
                            sort={sort}
                            setSort={setSort}
                          />
                          <SortHeader
                            label="Pipeline"
                            k="title"
                            sort={sort}
                            setSort={setSort}
                          />
                          <SortHeader
                            label="Type"
                            k="kind"
                            sort={sort}
                            setSort={setSort}
                          />
                          <th className="px-2 py-2 text-left font-normal">
                            Planning
                          </th>
                          <SortHeader
                            label="Dernière"
                            k="last"
                            sort={sort}
                            setSort={setSort}
                          />
                          <SortHeader
                            label="Prochaine"
                            k="next"
                            sort={sort}
                            setSort={setSort}
                          />
                          <th className="px-2 py-2 text-left font-normal">
                            Historique
                          </th>
                          <SortHeader
                            label="Échecs 7 j"
                            k="failures"
                            sort={sort}
                            setSort={setSort}
                            align="right"
                          />
                          <SortHeader
                            label="Documents"
                            k="docs"
                            sort={sort}
                            setSort={setSort}
                            align="right"
                          />
                          <SortHeader
                            label="24 h"
                            k="d24"
                            sort={sort}
                            setSort={setSort}
                            align="right"
                          />
                          <SortHeader
                            label="7 j"
                            k="d7"
                            sort={sort}
                            setSort={setSort}
                            align="right"
                          />
                          <th className="px-2 py-2 text-right font-normal">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      {groups.map((g) => {
                        const by = HEALTH_ORDER.map(
                          (h) =>
                            [
                              h,
                              g.rows.filter((p) => p.health === h).length
                            ] as const
                        ).filter(([, n]) => n > 0);
                        return (
                          <tbody key={g.key}>
                            <tr className="border-b bg-muted/50">
                              <th
                                scope="rowgroup"
                                colSpan={13}
                                className="px-3 py-2 text-left"
                              >
                                <span className="flex flex-wrap items-center gap-2">
                                  <span aria-hidden="true">{g.flag}</span>
                                  <span className="font-semibold">
                                    {g.label}
                                  </span>
                                  <span className="text-xs font-normal text-muted-foreground">
                                    {g.rows.length} pipeline
                                    {g.rows.length > 1 ? 's' : ''}
                                  </span>
                                  {by.map(([h, n]) => (
                                    <span
                                      key={h}
                                      className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${HEALTH[h].cls}`}
                                    >
                                      {n} {HEALTH[h].label.toLowerCase()}
                                    </span>
                                  ))}
                                </span>
                              </th>
                            </tr>
                            {g.rows.map((p) => (
                              <PipelineRow
                                key={p.name}
                                p={p}
                                data={data}
                                expanded={expanded.has(p.name)}
                                toggle={() => toggle(p.name)}
                                busy={busy === p.name}
                                act={canOperate ? act : undefined}
                                openLogs={canOperate ? openLogs : undefined}
                              />
                            ))}
                          </tbody>
                        );
                      })}
                    </table>
                  </section>
                )}

                {/* Cartes */}
                {view === 'cards' &&
                  groups.map((g) => (
                    <section key={g.key} aria-label={g.label}>
                      <h2 className="mb-3 flex items-center gap-2 font-semibold">
                        <span aria-hidden="true">{g.flag}</span> {g.label}
                        <span className="text-sm font-normal text-muted-foreground">
                          {g.rows.length}
                        </span>
                      </h2>
                      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                        {g.rows.map((p) => (
                          <PipelineCard
                            key={p.name}
                            p={p}
                            data={data}
                            busy={busy === p.name}
                            act={canOperate ? act : undefined}
                            openLogs={canOperate ? openLogs : undefined}
                          />
                        ))}
                      </div>
                    </section>
                  ))}

                {/* Collections */}
                <section
                  aria-labelledby="lf-collections"
                  className="rounded-2xl border bg-card p-5 shadow-sm"
                >
                  <h2
                    id="lf-collections"
                    className="flex items-center gap-2 font-semibold"
                  >
                    <Database className="h-4 w-4" aria-hidden="true" />{' '}
                    Collections
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Volumes Qdrant ; les écarts se calculent à partir des
                    relevés horaires de LegalFlow.
                  </p>
                  <div className="mt-3 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-xs text-muted-foreground">
                          <th className="py-2 font-normal">Collection</th>
                          <th className="py-2 text-right font-normal">
                            Documents
                          </th>
                          <th className="py-2 text-right font-normal">24 h</th>
                          <th className="py-2 text-right font-normal">
                            7 jours
                          </th>
                          <th className="py-2 pl-4 font-normal">
                            Alimentée par
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(data.collections)
                          .sort(
                            (a, b) => (b[1].count ?? -1) - (a[1].count ?? -1)
                          )
                          .map(([name, s]) => (
                            <tr key={name} className="border-b last:border-0">
                              <td className="py-2 font-mono text-xs">{name}</td>
                              <td className="py-2 text-right tabular-nums">
                                {s.count != null
                                  ? NF.format(s.count)
                                  : 'absente'}
                              </td>
                              <td className="py-2 text-right tabular-nums">
                                <Delta value={s.delta_24h} />
                              </td>
                              <td className="py-2 text-right tabular-nums">
                                <Delta value={s.delta_7d} />
                              </td>
                              <td className="py-2 pl-4 text-xs text-muted-foreground">
                                {data.pipelines
                                  .filter((p) => p.collections.includes(name))
                                  .map((p) => p.title)
                                  .join(', ') || 'ingestion ponctuelle'}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              </>
            )}
          </div>
        </div>

        <Dialog open={!!logs} onOpenChange={(o) => !o && setLogs(null)}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-4 w-4" aria-hidden="true" /> Logs
              </DialogTitle>
              <DialogDescription className="font-mono text-xs">
                {logs?.job}
              </DialogDescription>
            </DialogHeader>
            {logs?.error ? (
              <p className="text-sm text-red-700 dark:text-red-300">
                {logs.error}
              </p>
            ) : logs?.text == null ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />{' '}
                Chargement…
              </div>
            ) : (
              <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap rounded-lg bg-muted p-3 font-mono text-xs leading-relaxed">
                {logs.text}
              </pre>
            )}
          </DialogContent>
        </Dialog>
      </>
    </Page>
  );
}
